import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { dailyStandupCostPath, digestExamplesPath, meetingWastePath, rootPath, signInPath } from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const CAMPAIGN = 'utm_source=async-standup&utm_campaign=playbook'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

const COMPARISON = [
  {
    label: 'Update',
    live: 'Spoken, with everyone present',
    form: 'Typed into a bot form',
    digest: 'Built from commits and pull requests',
  },
  {
    label: 'Source of truth',
    live: 'What people remember',
    form: 'Whatever someone typed',
    digest: 'The code that landed',
  },
  {
    label: 'Timezones',
    live: 'One meeting slot',
    form: 'Flexible, if the form gets filled',
    digest: 'Flexible. The digest waits',
  },
  {
    label: 'Blockers',
    live: 'Raised live, if time remains',
    form: 'A field that is easy to skip',
    digest: 'Stalled work shows up. A person still replies',
  },
  {
    label: 'Common failure',
    live: 'Overruns and status theater',
    form: 'Unread forms and bot fatigue',
    digest: 'A quiet repo looks like a quiet day',
  },
  {
    label: 'Use it when',
    live: 'One timezone, and the call unblocks people',
    form: 'The work is not in git yet',
    digest: 'The team already ships in GitHub',
  },
] as const

const ROLLOUT = [
  {
    title: 'Pick a cadence',
    body: 'Match the rhythm you already have. Daily if you stand up every weekday. Weekly if the live meeting was already a status dump and the work moves slower. Pick one. Do not run a daily form and a daily meeting.',
  },
  {
    title: 'Decide what to post',
    body: 'Three lines: what landed, what is next, and what is blocked. If the work is in a pull request or a commit, link it. Skip the paragraph that restates the commit message.',
  },
  {
    title: 'Put it in one place',
    body: 'One Slack channel, or one email, not a channel plus a doc plus a bot DM. The update has to be findable tomorrow. A commit digest posts to Slack or email so the place stays the same every day.',
  },
  {
    title: 'Respect timezones',
    body: 'Set a read-by time, not a meet-at time. Each person posts by the end of their working day and reads the thread at the start of theirs. A teammate in Lisbon and a teammate in San Francisco share one thread. Nobody waits on a call.',
  },
  {
    title: 'Handle blockers in the open',
    body: 'A blocker needs an owner and a reply, not a line that sits in a form. Name the person or the decision, in the same thread, the same day. If two or three people need to talk, book that conversation. Do not pull the whole team back for one stuck review.',
  },
] as const

const FAILURES = [
  {
    title: 'Status theater',
    body: 'People write what sounds productive instead of what shipped. Prefer links to commits and pull requests over adjectives. If the update could be pasted unchanged for a week, it is theater.',
  },
  {
    title: 'Forms nobody reads',
    body: 'A bot asks three questions, people answer, and the answers land in a thread one manager skims. If the only reader is one person, you built a status report, not a standup. Put the update where the team already looks.',
  },
  {
    title: 'Bot fatigue',
    body: 'Another Slack app, another reminder, another form. Engineers already wrote the status when they committed. Asking them to type it again trains people to paste "no update" or to mute the bot.',
  },
] as const

function campaignPath(path: string) {
  return `${path}?${CAMPAIGN}`
}

export function AsyncStandupArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">Async standup</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          An async standup is a short written update the team reads on its own time, instead of a live daily standup.
          Here is when that beats a calendar invite, how to roll it out, and how form bots compare with a commit
          digest.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          Already paying for a live standup? See the{' '}
          <Link href={dailyStandupCostPath()} className={`${linkClass} font-medium`}>
            daily standup cost
          </Link>{' '}
          by team size, or open the{' '}
          <Link href={meetingWastePath()} className={`${linkClass} font-medium`}>
            meeting cost calculator
          </Link>{' '}
          and plug in your salary.
        </p>
        <div className="mt-5">
          <Button asChild size="lg" className="bg-golden-500 hover:bg-golden-600 font-semibold text-black">
            <Link href={meetingWastePath()}>
              Open the calculator
              <ArrowRight className="ml-2 size-4 shrink-0" />
            </Link>
          </Button>
        </div>
      </Card>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">The next step after the calculator</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Measured the meeting already? This playbook is the next step. The{' '}
            <Link href={meetingWastePath()} className={linkClass}>
              wasted money meeting calculator
            </Link>{' '}
            turns salary, attendees, and duration into an annual figure. The{' '}
            <Link href={dailyStandupCostPath()} className={linkClass}>
              daily standup cost
            </Link>{' '}
            page does that for a 15-minute daily ritual, by team size. The sections below are how to run the update in
            writing: cadence, timezones, and blockers, without pulling the whole team onto a call.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What an async standup is</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            People post what shipped, what is next, and what is blocked. Teammates read it when they start work, in
            their own timezone. It is still a standup. The point is alignment and blockers, not a novel. The difference
            is the medium: text in Slack, email, or a digest, instead of a meeting everyone has to attend.
          </p>
          <p>
            <Link href={rootPath()} className={linkClass}>
              Git Digest
            </Link>{' '}
            is one way to produce that update. It turns GitHub activity into daily or weekly team commit digests for
            Slack and email. It is not Gitingest. Gitingest turns a repository into text for LLMs. Git Digest is a team
            product for engineering leads who want the standup to come from the code that landed. Annotated lines from
            public changelogs are on the{' '}
            <Link href={digestExamplesPath()} className={linkClass}>
              digest examples
            </Link>{' '}
            page.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">When it beats a live daily standup</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A live daily standup still works for a small team in one timezone that finishes in about 15 minutes and
            uses the time to unblock someone. It starts to lose when the team spans timezones, when people only report
            work that is already in git, or when the 15 minutes regularly becomes 25.
          </p>
          <p>
            Price the ritual before you defend it. A five-person daily standup at a $100/hour fully-loaded rate is
            $30,000 a year before overrun: 5 people × (15 ÷ 60) hours × 5 days a week × 48 working weeks × $100/hour.
            That figure uses the same payroll formula as the{' '}
            <Link href={dailyStandupCostPath()} className={linkClass}>
              daily standup cost
            </Link>{' '}
            page. It is modeled meeting cost, not a survey. The{' '}
            <Link href={meetingWastePath()} className={linkClass}>
              meeting cost calculator
            </Link>{' '}
            lets you change salary, attendance, and duration.
          </p>
          <p>
            Async wins when the update can be read, the blocker can be routed to the right person, and the rest of the
            team does not need to sit through it.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div className="space-y-4">
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">How to roll an async standup out</h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Pause the live standup for a trial. Tell the team it is paused, not deleted, and that you will look at it
            again after people have used the written update for real.
          </p>
        </div>
        <ol className="list-decimal space-y-8 pl-5">
          {ROLLOUT.map((step) => (
            <li key={step.title} className="space-y-2">
              <h3 className="text-foreground text-xl tracking-tight">{step.title}</h3>
              <p className="text-muted-foreground text-base leading-relaxed">{step.body}</p>
            </li>
          ))}
          <li className="space-y-2">
            <h3 className="text-foreground text-xl tracking-tight">Know when to still meet</h3>
            <p className="text-muted-foreground text-base leading-relaxed">
              Keep a live meeting for incidents, a design disagreement, the first week for a new teammate, and planning
              that is not a status round. Those meetings have a purpose. The daily status round is the part you can
              drop. If someone wants the live standup back just in case, price it with the{' '}
              <Link href={meetingWastePath()} className={linkClass}>
                meeting cost calculator
              </Link>{' '}
              first.
            </p>
          </li>
        </ol>
      </section>

      <section className="space-y-6">
        <div className="space-y-4">
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Where async standups go wrong</h2>
          <p className="text-muted-foreground text-base leading-relaxed">
            Most failed rollouts are not a tooling problem. They are a reading problem, a writing problem, or one more
            bot.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {FAILURES.map((failure) => (
            <Card key={failure.title} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 text-lg">{failure.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{failure.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-muted-foreground text-base leading-relaxed">
          Use a form only for work that is not in git yet: research, interviews, or a blocked deploy that has no
          commit. If the status is already a commit, do not ask for it twice.
        </p>
      </section>

      <section id="standup-comparison" className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">
          Live standup vs form bots vs commit digests
        </h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Range, Atlassian, and Parabol publish guides for the live daily standup. Geekbot and Standuply are form
            bots: they ask yesterday, today, and blockers. A commit digest skips the form when the code is already the
            status.{' '}
            <Link href={rootPath()} className={linkClass}>
              Git Digest
            </Link>{' '}
            sits in that third column: GitHub activity, delivered to Slack or email.
          </p>
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[44rem] text-left text-sm">
            <caption className="bg-muted/40 text-muted-foreground px-4 py-3 text-left text-xs leading-relaxed">
              Three ways engineering teams run a standup. A commit digest still needs a human reply when someone is
              blocked.
            </caption>
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  <span className="sr-only">Topic</span>
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Live daily standup
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Form or status bot
                </th>
                <th scope="col" className="bg-golden-500/15 px-4 py-3 font-semibold">
                  Commit digest
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.label} className="border-t">
                  <th scope="row" className="text-foreground px-4 py-3 font-medium">
                    {row.label}
                  </th>
                  <td className="text-muted-foreground px-4 py-3">{row.live}</td>
                  <td className="text-muted-foreground px-4 py-3">{row.form}</td>
                  <td className="bg-golden-500/10 text-foreground px-4 py-3">{row.digest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">
          A quiet repository is not the same as a quiet day. Design, support, and hiring do not show up as commits.
          Keep a one-line note for that work, and let the digest cover the rest.
        </p>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Async standup FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Common questions about replacing a live daily standup, posting across timezones, and skipping form bots.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Replace the standup with GitHub digests</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub and get daily or weekly team commit summaries in Slack and email. Same alignment, without the
          status meeting. Then{' '}
          <Link href={meetingWastePath()} className={linkClass}>
            price any meeting you still keep
          </Link>
          .
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={campaignPath(signInPath())}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
